import { React, useState, useEffect, Drawer, DialogActions, Box, Typography, Divider, IconButton, CustomRadioButton, Default_Text_Color } from "../../utils/globalImports";
import { CustomButton, CustomTextField, CustomMultiSelectDropdown, validationMessages, ArrowBackIosIcon } from "../../utils/globalImports";

interface PersonnelData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
  userType: number[];
  status: string;
}

interface CustomAddPersonnelModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: PersonnelData, setBackendErrors?: (errors: any) => void) => void;
  personnelData?: PersonnelData;
  handleContentChange:(state:boolean)=>void
}

const validationMessageStyle = {
  color: "rgba(255, 0, 0, 0.99)",
  fontSize: "11px",
  paddingLeft: "10px",
  paddingBottom: "10px",
};

const userTypeOptions = [
  { value: 1, text: "Technical Project Lead" },
  { value: 2, text: "Assistant Technical Project Lead" },
];

const CustomAddPersonnelModal: React.FC<CustomAddPersonnelModalProps> = ({
  open,
  onClose,
  onSubmit,
  personnelData,
  handleContentChange
}) => {
  const [formData, setFormData] = useState<PersonnelData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    userType: [],
    status: 'Active', 
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    userType: "",
  });
  
  const [backendErrors, setBackendErrors] = useState<{ [key: string]: string }>({});

  const validate = () => {
    let isValid = true;
    const newErrors = { firstName: "", lastName: "", email: "", phoneNo: "", userType: "" };
    const numberRegex = /\d/;

    if (!formData.firstName.trim()) {
      newErrors.firstName = validationMessages.common_required("First Name");
      isValid = false;
    } else {
      if (numberRegex.test(formData.firstName)) {
        newErrors.firstName = validationMessages.common_invalidString("First Name");
        isValid = false;
      }
      else if (formData.firstName.length > 100) {
        newErrors.firstName = validationMessages.common_characterLimit("First Name", 100);
        isValid = false;
      }
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = validationMessages.common_required("Last Name");
      isValid = false;
    } else {
      if (numberRegex.test(formData.lastName)) {
        newErrors.lastName = validationMessages.common_invalidString("Last Name");
        isValid = false;
      }
      else if (formData.lastName.length > 100) {
        newErrors.lastName = validationMessages.common_characterLimit("Last Name", 100);
        isValid = false;
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = validationMessages.common_required("Email");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = validationMessages.common_email("Email");
      isValid = false;
    }

    if(formData.phoneNo.trim() && !/^[0-9]+$/.test(formData.phoneNo)) {
      newErrors.phoneNo = validationMessages.common_invalidNumber("Phone No.");
      isValid = false;
    } else if (formData.phoneNo.length > 15) {
      newErrors.phoneNo = validationMessages.common_characterLimit("Phone No.", 15);
      isValid = false;
    }

    if (formData.userType.length === 0) {
      newErrors.userType = validationMessages.common_required("User Type");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  
  useEffect(() => {
    if (open) {
      if (personnelData) {
        setFormData({
          firstName: personnelData.firstName || "",
          lastName: personnelData.lastName || "",
          email: personnelData.email || "",
          phoneNo: personnelData.phoneNo || "",
          userType: personnelData.userType || [],
          status: personnelData.status || "Active",
        });
      } else {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNo: "",
          userType: [],
          status: "Active",
        });
      }
      setErrors({ firstName: "", lastName: "", email: "", phoneNo: "", userType: "" });
      setBackendErrors({});
    }
  }, [open, personnelData]);

  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData({
        ...formData,
        [name]: value,
      });
      handleContentChange(true);
    };

  const handleUserTypeChange = (selectedValues: (string | number)[]) => {
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        userType: selectedValues.map((val) => Number(val)), 
      };
      handleContentChange(true);
      return updatedFormData;
    });
  };


  const handleSubmit = async() => {
    if (validate()) {
      setBackendErrors({});
      await onSubmit(formData, setBackendErrors); 
    }
    
  };
  
  
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: 350,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: 500,
          height: "100%",
          padding: 2,
          backgroundColor: "rgba(255, 255, 255, 0.86)",
        },
      }}
    >
      <Box>
        <Typography 
        sx={{ 
           color: Default_Text_Color,
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          mb: 2,
          mt: 2,
          position: 'relative',
          fontWeight: 500,
          fontSize: 22
         }}
        >
          <IconButton sx={{ position: "absolute", left: 0 }} onClick={onClose}>
            <ArrowBackIosIcon sx={{ color: "#1e66ab", fontSize: "15px" }} />
          </IconButton>
          {personnelData ? "Edit Personnel" : "Add Personnel"}
        </Typography>

        <Divider sx={{ mb: 3, mx: "-15px" }} />

        <CustomTextField label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} sx={{ mb: 2, mt:1.5, backgroundColor: '#fff' }} required />
        {errors.firstName && <Typography style={validationMessageStyle}>{errors.firstName}</Typography>}

        <CustomTextField label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} sx={{ mb: 2, mt:1.5, backgroundColor: '#fff' }} required />
        {errors.lastName && <Typography style={validationMessageStyle}>{errors.lastName}</Typography>}

        <CustomTextField label="Email Address" name="email" value={formData.email} onChange={handleChange} sx={{ mb: 2, mt:1.5, backgroundColor: '#fff' }} required />
        {errors.email && <Typography style={validationMessageStyle}>{errors.email}</Typography>}
        {backendErrors.email && <Typography style={validationMessageStyle}>{backendErrors.email}</Typography>}

        <CustomTextField label="Phone No." name="phoneNo" value={formData.phoneNo} onChange={handleChange} sx={{ mb: 2, mt:1.5, backgroundColor: '#fff' }} />
        {errors.phoneNo && <Typography style={validationMessageStyle}>{errors.phoneNo}</Typography>}

        <CustomMultiSelectDropdown selectedValues={formData.userType} onChange={handleUserTypeChange} label="User Type" options={userTypeOptions} requiredLabel sx={{ mt: 1, mb: 2, '& .MuiTypography-root': { mb: 1.5 },  '& .MuiInputLabel-root': { } }}/>
        {errors.userType && <Typography style={validationMessageStyle}>{errors.userType}</Typography>}

        {personnelData && (
          <>
            <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
            <Box sx={{ display: 'flex' }}>
              <CustomRadioButton
                label="Active"
                value="Active"
                name="status"
                checked={formData.status === 'Active'} 
                onChange={handleChange}
                required
              />
              <CustomRadioButton
                label="Inactive"
                value="Inactive"
                name="status"
                checked={formData.status === 'Inactive'} 
                onChange={handleChange}
                required
              />
            </Box>
          </>
        )}

        <DialogActions>
          <CustomButton 
            capsule 
            sx={{
              textTransform: 'none',
              width: '282px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              mr: 'auto',
              ml: 'auto',
              mt: 4,
            }} 
            onClick={handleSubmit}>
              {personnelData ? "Update Personnel" : "Add Personnel"}
              </CustomButton>
        </DialogActions>
      </Box>
    </Drawer>
  );
};

export default CustomAddPersonnelModal;